import React from "react";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";

import { useTenantTranslation } from "hooks/formatters";
import {
  useGetSearchQuery,
  useRenameSessionMutation,
} from "src/store/api/generatedApi";
import { RenameSessionModal } from "../../searches/RenameSessionModal";
import { useTenant } from "hooks/settings";
import { useFormatLocalDateString } from "hooks/useFormatLocalDate";
import { hoverEmail, userDisplayName } from "src/utils/userDisplay";

interface Props {
  id: number;
}

export const SearchDetails = ({ id }: Props) => {
  const theme = useTheme();
  const formatDate = useFormatLocalDateString("datetime");
  const { t } = useTenantTranslation();
  const tenantName = useTenant();

  const [openModal, setOpenModal] = React.useState(false);
  const [renameSession] = useRenameSessionMutation();
  const search = useGetSearchQuery({ tenantName, searchId: id });

  const handleUpdateName = async ({
    tenantName,
    sessionId,
    name,
  }: {
    tenantName: string;
    sessionId: number;
    name: string;
  }) => {
    await renameSession({
      tenantName,
      sessionId,
      renameSession: { name },
    }).unwrap();
  };

  return search.isSuccess ? (
    <>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap={0.25}
        sx={{ minWidth: 0, flex: 1 }}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography
            variant="body1"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {search.data.session_name}
          </Typography>
          <IconButton
            size="small"
            color="inherit"
            onClick={() => {
              setOpenModal(true);
            }}
            sx={{ padding: 0, flexShrink: 0 }}
          >
            <EditIcon fontSize="small" sx={{ alignSelf: "center" }} />
          </IconButton>
        </Box>
        <Tooltip title={hoverEmail(search.data.session_created_by)}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.grey[400],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {t("createdAtBy", {
              time: formatDate(search.data.session_created_at),
              name: userDisplayName(search.data.session_created_by),
            })}
          </Typography>
        </Tooltip>
      </Box>
      <RenameSessionModal
        originalName={search.data.session_name}
        prefillName={search.data.session_name}
        open={openModal}
        close={() => {
          setOpenModal(false);
        }}
        rename={(name) =>
          handleUpdateName({
            tenantName,
            sessionId: search.data.session_id,
            name,
          })
        }
        refetch={search.refetch}
      />
    </>
  ) : null;
};
